import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import AddToCartControl from "@/components/AddToCartControl";

function formatDateRange(deb, fin) {
    if (!deb || !fin) return null;
    try {
        const d1 = new Date(deb);
        const d2 = new Date(fin);
        // Same month: "12 au 18 février 2026"
        const opts = { day: "numeric", month: "long", year: "numeric" };
        const sameMonth = d1.getMonth() === d2.getMonth() && d1.getFullYear() === d2.getFullYear();
        if (sameMonth) {
            const day1 = d1.toLocaleDateString("fr-CH", { day: "numeric" });
            const rest = d2.toLocaleDateString("fr-CH", opts);
            return `${day1} au ${rest}`;
        }
        return `${d1.toLocaleDateString("fr-CH", opts)} au ${d2.toLocaleDateString("fr-CH", opts)}`;
    } catch {
        return null;
    }
}

function formatPrice(s) {
    if (s.prix == null || s.prix === "") return null;
    const value = Number(s.prix).toFixed(2);
    return `CHF ${value} / ${s.unite || "kg"}`;
}

export default function Suggestions() {
    const { data: raw = [], isLoading } = useQuery({
        queryKey: ["suggestions"],
        queryFn: async () => (await api.get("/suggestions")).data,
    });

    // Only display seasonal suggestions (mot du boucher is intentionally hidden here)
    const suggestions = raw.filter((s) => s.type !== "mot_du_boucher");

    return (
        <section className="max-w-6xl mx-auto px-5 md:px-10 py-12 md:py-16" data-testid="suggestions-page">
            <p className="bc-label-small">Espace Pro</p>
            <h1 className="font-serif text-[#D4BC7C] text-4xl sm:text-5xl mt-4">Suggestions du moment</h1>
            <div className="bc-hairline-gold my-6" />

            {isLoading && (
                <p className="text-[#D4BC7C] font-serif tracking-widest animate-pulse">Chargement...</p>
            )}

            {suggestions.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                    {suggestions.map((s) => {
                        const range = formatDateRange(s.date_debut, s.date_fin);
                        const price = formatPrice(s);
                        return (
                            <article
                                key={s.id}
                                className="bc-card p-0 overflow-hidden flex flex-col"
                                data-testid={`suggestion-${s.id}`}
                            >
                                {s.image && (
                                    <div
                                        className="w-full h-56 bg-cover bg-center border-b"
                                        style={{
                                            backgroundImage: `url(${s.image})`,
                                            borderColor: "var(--bc-border)",
                                        }}
                                    />
                                )}
                                <div className="p-6 md:p-8 flex-1 flex flex-col">
                                    <h3 className="font-serif text-[#D4BC7C] text-2xl">{s.titre}</h3>
                                    {range && (
                                        <p
                                            className="text-xs uppercase tracking-[0.18em] text-[#A0A0A0] mt-2"
                                            data-testid={`suggestion-week-${s.id}`}
                                        >
                                            Semaine du {range}
                                        </p>
                                    )}
                                    <div className="bc-hairline-gold my-4" />
                                    <p className="text-[#F2ECE0]/85 text-sm leading-relaxed whitespace-pre-line flex-1">
                                        {s.contenu}
                                    </p>
                                    {price && (
                                        <div
                                            className="mt-6 pt-4 border-t"
                                            style={{ borderColor: "var(--bc-border)" }}
                                            data-testid={`suggestion-price-${s.id}`}
                                        >
                                            <div className="flex items-baseline justify-between mb-4">
                                                <span className="text-[10px] uppercase tracking-[0.22em] text-[#A0A0A0]">
                                                    Prix HT
                                                </span>
                                                <span className="font-serif text-[#D4BC7C] text-xl">{price}</span>
                                            </div>
                                            <div className="flex justify-end">
                                                <AddToCartControl
                                                    item={{
                                                        id: `sug-${s.id}`,
                                                        code: `SUG-${s.id.slice(0, 4).toUpperCase()}`,
                                                        article: s.titre,
                                                        prix_kg_ht: Number(s.prix),
                                                        unite: s.unite || "kg",
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}

            {!isLoading && suggestions.length === 0 && (
                <p className="text-[#A0A0A0] mt-10" data-testid="suggestions-empty">
                    Aucune suggestion publiée pour le moment.
                </p>
            )}
        </section>
    );
}
